/**
 * guidelinesApiService.ts
 *
 * Direct HTTP client for the Guidelines Lambda Function URL.
 * Endpoints:
 *   POST /guidelines/document_verification
 *   POST /guidelines/review
 *   POST /title-search/property_tax_search
 *   POST /title-search/recorded_document_search
 *   POST /title-analysis/review
 *   GET /title-analysis/results/{job_id}
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import type { TitleAnalysisReport, TitleAnalysisStatus } from '../titleAnalysis';

const GUIDELINES_API_URL = import.meta.env.VITE_GUIDELINES_API_URL as string | undefined;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GuidelineReviewRequest {
  guideline_id: string;
  job_id: string;
}

/** Shape returned by POST /guidelines/document_verification */
export type DocumentVerificationResult = Record<
  string,
  { status: boolean; reason?: string }
>;

/** Shape returned by POST /guidelines/review */
export interface LoanFindingRaw {
  loan_id: string;
  status: 'PASS' | 'FAIL' | 'WARNING' | 'FAILED' | 'MISSING DATA';
  explanation: string;
  unmet_criteria?: string[];
}

export interface GuidelineReviewResultRaw {
  guideline_id: string;
  findings: LoanFindingRaw[];
}

export interface CachedGuidelineResults {
  job_id: string;
  guideline_id: string;
  document_verification_results: DocumentVerificationResult | null;
  guideline_review_results: GuidelineReviewResultRaw | null;
}

// ─── Title Search dispatch types ────────────────────────────────────────────────

/** Request body for the title-search agent dispatch routes. */
export interface TitleSearchDispatchRequest {
  job_id: string;
}

/**
 * Result of dispatching a title-search browser agent.
 *
 * The routes are fire-and-return: they mark the step `in_progress`, dispatch the
 * long-running agent asynchronously, and respond `202 Accepted`. `accepted` is
 * `true` when the backend acknowledged the dispatch; `jobId` echoes the job when
 * present in the response body (the body may be empty for a bare 202).
 */
export interface TitleSearchDispatchResult {
  accepted: boolean;
  status: number;
  jobId?: string;
}

// ─── Title Analysis types ──────────────────────────────────────────────────────

/** Request body for the title analysis route. */
export interface TitleAnalysisRequest {
  job_id: string;
  use_case?: string;
}

/** Response shape from POST /title-analysis/review */
export interface TitleAnalysisDispatchResult {
  status: 'accepted' | string;
  job_id: string;
  already_running?: boolean;
}

/** Response shape from GET /title-analysis/results/{job_id} */
export interface TitleAnalysisResultsResponse {
  job_id: string;
  title_analysis_status: TitleAnalysisStatus;
  title_analysis_error: string | null;
  title_analysis_started_at: number | null;
  stale_after_seconds: number;
  generated_at: string | null;
  report: TitleAnalysisReport | null;
}

// ─── Error Types ──────────────────────────────────────────────────────────────

/**
 * Error thrown by guidelines API helpers on non-2xx responses.
 * Carries the HTTP status and the server's message (JSON `message` field or raw text).
 */
export class GuidelinesApiError extends Error {
  status: number;
  serverMessage: string;

  constructor(status: number, serverMessage: string, message?: string) {
    super(message ?? `Guidelines API error ${status}: ${serverMessage}`);
    this.name = 'GuidelinesApiError';
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No auth token available');
  return token;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const baseUrl = GUIDELINES_API_URL?.replace(/\/$/, '');
  if (!baseUrl) {
    throw new Error(
      'VITE_GUIDELINES_API_URL is not configured. ' +
        'Set it in your .env file to the Guidelines Lambda Function URL.'
    );
  }

  const token = await getAuthToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    let serverMessage = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed.message && typeof parsed.message === 'string') {
        serverMessage = parsed.message;
      }
    } catch {
      // Not JSON, use raw text
    }
    throw new GuidelinesApiError(response.status, serverMessage);
  }

  return response.json() as Promise<T>;
}

/**
 * POST to a fire-and-return dispatch route that responds `202 Accepted`.
 *
 * Unlike `post`, this does not assume a JSON result payload: the dispatch routes
 * may return a bare `202` with an empty body. The response body is parsed
 * leniently (empty/non-JSON bodies are tolerated) so callers get a consistent
 * dispatch result regardless of whether the backend echoes the job.
 */
async function postAccepted(
  path: string,
  body: unknown
): Promise<TitleSearchDispatchResult> {
  const baseUrl = GUIDELINES_API_URL?.replace(/\/$/, '');
  if (!baseUrl) {
    throw new Error(
      'VITE_GUIDELINES_API_URL is not configured. ' +
        'Set it in your .env file to the Guidelines Lambda Function URL.'
    );
  }

  const token = await getAuthToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    let serverMessage = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed.message && typeof parsed.message === 'string') {
        serverMessage = parsed.message;
      }
    } catch {
      // Not JSON, use raw text
    }
    throw new GuidelinesApiError(response.status, serverMessage);
  }

  // The dispatch routes return 202 Accepted, optionally with a small JSON body
  // echoing the job. Tolerate an empty or non-JSON body.
  const raw = await response.text().catch(() => '');
  let jobId: string | undefined;
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { job_id?: string; jobId?: string };
      jobId = parsed.job_id ?? parsed.jobId;
    } catch {
      // Non-JSON body (e.g. plain "Accepted") — no job id to extract.
    }
  }

  return { accepted: response.status === 202, status: response.status, jobId };
}

async function get<T>(path: string): Promise<T> {
  const baseUrl = GUIDELINES_API_URL?.replace(/\/$/, '');
  if (!baseUrl) {
    throw new Error(
      'VITE_GUIDELINES_API_URL is not configured. ' +
        'Set it in your .env file to the Guidelines Lambda Function URL.'
    );
  }

  const token = await getAuthToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    let serverMessage = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed.message && typeof parsed.message === 'string') {
        serverMessage = parsed.message;
      }
    } catch {
      // Not JSON, use raw text
    }
    throw new GuidelinesApiError(response.status, serverMessage);
  }

  return response.json() as Promise<T>;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Run document verification for a job against a guideline.
 * Calls POST /guidelines/document_verification
 */
export async function runDocumentVerification(
  req: GuidelineReviewRequest
): Promise<DocumentVerificationResult> {
  return post<DocumentVerificationResult>('/guidelines/document_verification', req);
}

/** Shape returned by POST /guidelines/consistency_verification */
export type ConsistencyVerificationResult = Record<
  string,
  { status: boolean; reason?: string }
>;

/**
 * Run consistency verification for a job against a guideline.
 * Calls POST /guidelines/consistency_verification
 */
export async function runConsistencyVerification(
  req: GuidelineReviewRequest
): Promise<ConsistencyVerificationResult> {
  return post<ConsistencyVerificationResult>('/guidelines/consistency_verification', req);
}

/**
 * Run investor guideline review for a job.
 * Calls POST /guidelines/review
 * Returns findings keyed by loan_id.
 */
export async function runGuidelineReview(
  req: GuidelineReviewRequest
): Promise<GuidelineReviewResultRaw> {
  const response = await post<{ result: GuidelineReviewResultRaw }>('/guidelines/review', req);
  return response.result;
}

/**
 * Fetch cached guideline results (both document verification and review) from DynamoDB.
 * Calls GET /guidelines/results/:job_id/:guideline_id
 * Returns null for results that haven't been run yet.
 */
export async function getCachedGuidelineResults(
  jobId: string,
  guidelineId: string
): Promise<CachedGuidelineResults> {
  return get<CachedGuidelineResults>(`/guidelines/results/${jobId}/${guidelineId}`);
}

// ─── Title Search agent dispatch ────────────────────────────────────────────────

/**
 * Dispatch the Property Tax Search agent for a Title Search job.
 * Calls POST /title-search/property_tax_search.
 *
 * Fire-and-return: the backend marks the property tax search step `in_progress`,
 * dispatches the browser agent asynchronously, and responds `202 Accepted`. The
 * frontend then polls `getTitleAnalysisResults` until the step advances to a
 * terminal status.
 */
export async function runPropertyTaxSearch(
  req: TitleSearchDispatchRequest
): Promise<TitleSearchDispatchResult> {
  return postAccepted('/title-search/property_tax_search', req);
}

/**
 * Dispatch the Recorded Document Search agent for a Title Search job.
 * Calls POST /title-search/recorded_document_search.
 *
 * Fire-and-return: the backend marks the recorded document search step
 * `in_progress`, dispatches the browser agent asynchronously, and responds
 * `202 Accepted`. The frontend then polls `getJob` (see `shouldContinuePolling`)
 * until the step advances to a terminal status.
 */
export async function runRecordedDocumentSearch(
  req: TitleSearchDispatchRequest
): Promise<TitleSearchDispatchResult> {
  return postAccepted('/title-search/recorded_document_search', req);
}

// ─── Title Analysis ────────────────────────────────────────────────────────────

/**
 * Start a title analysis for a Title Search job.
 * Calls POST /title-analysis/review.
 *
 * Fire-and-return: the backend marks the title analysis step `in_progress`,
 * dispatches the AI agent asynchronously, and responds `202 Accepted`. The
 * frontend then polls `getTitleAnalysisResults` until the step advances to a
 * terminal status.
 */
export async function runTitleAnalysis(
  req: TitleAnalysisRequest
): Promise<TitleAnalysisDispatchResult> {
  return post<TitleAnalysisDispatchResult>('/title-analysis/review', req);
}

/**
 * Fetch title analysis results from DynamoDB.
 * Calls GET /title-analysis/results/{job_id}.
 * Returns null report and generated_at when none is stored.
 */
export async function getTitleAnalysisResults(
  jobId: string
): Promise<TitleAnalysisResultsResponse> {
  return get<TitleAnalysisResultsResponse>(`/title-analysis/results/${encodeURIComponent(jobId)}`);
}
